import type { RecordResult } from '../../@types/records';
import type { HistoryFieldType } from '../../components/record/util/record-component-util';
import { transformHistoryFieldsValue } from '../../components/record/util/record-component-util';

/**
 * Sync details structure stored in IndexedDB alongside offline record mutations.
 *
 * Contains metadata needed to transform records from local format to API format during sync.
 */
export interface SyncDetails {
  historyFields?: Array<HistoryFieldType>;
}

/**
 * Type definition for the transforms registry.
 * Ensures every key in SyncDetails has a corresponding transform function.
 */
type Transforms = {
  [K in keyof SyncDetails]-?: (record: RecordResult, payload: NonNullable<SyncDetails[K]>) => RecordResult;
};

/**
 * Registry of sync transforms.
 *
 * Each transform converts record data from local IndexedDB format to the format expected by the server API.
 */
const transforms: Transforms = {
  historyFields: transformHistoryFieldsValue,
};

/**
 * Applies all registered sync transforms to a record based on the provided sync details.
 *
 * @param record - The record to transform
 * @param syncDetails - Object containing transform keys and their payloads
 * @returns The transformed record ready for API submission
 */
export function applySyncTransforms(record: RecordResult, syncDetails?: SyncDetails): RecordResult {
  if (!syncDetails) return record;

  let result = record;

  for (const key of Object.keys(transforms) as Array<keyof SyncDetails>) {
    const payload = syncDetails[key];
    if (payload !== undefined) {
      result = transforms[key](result, payload);
    }
  }

  return result;
}
